import React, { useEffect, useState, useMemo, useRef } from "react";
import Swal from "sweetalert2";
import {
    collection,
    doc,
    getDoc,
    onSnapshot,
    query,
    where
} from "firebase/firestore";
import { auth, db } from "../../firebase";
import { onAuthStateChanged } from "firebase/auth";
import moment from "moment";

import {
    Box,
    Button,
    Card,
    CardContent,
    Container,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography,
    Chip,
    LinearProgress
} from "@mui/material";

// --- CUSTOM COLOR PALETTE & THEME ---
const COLORS = {
    darkGreen: '#06362D',
    calPolyGreen: '#155332',
    vanilla: '#DEE2B1',
    gold: '#FFD500',
    goldenrod: '#D1A505',
    bgDark: '#031c17',
    borderGreen: 'rgba(21, 83, 50, 0.6)'
};

const TEXT_SECONDARY = "rgba(222, 226, 177, 0.7)";

function Checkoutreport() {
    const printRef = useRef();
    const [bookings, setBookings] = useState([]);
    const [propertyId, setPropertyId] = useState(null);
    const [propData, setPropData] = useState({});
    const [username, setUsername] = useState("");
    const [searchText, setSearchText] = useState("");
    const [selectedDate, setSelectedDate] = useState(moment().format("YYYY-MM-DD"));
    const [loading, setLoading] = useState(true);

    const Toast = Swal.mixin({
        toast: true,
        position: "top-end",
        showConfirmButton: false,
        timer: 3000,
        timerProgressBar: true,
    });

    // ================= STEP 1: AUTH & PROPERTY IDENTIFICATION =================
    useEffect(() => {
        const unsub = onAuthStateChanged(auth, async (user) => {
            if (user) {
                try {
                    const docRef = doc(db, "authroles", user.uid);
                    const docSnap = await getDoc(docRef);

                    if (docSnap.exists()) {
                        const roleData = docSnap.data();
                        setUsername(roleData.name || user.displayName || "Staff");
                        const pId = roleData.propertyId;
                        setPropertyId(pId);

                        if (pId) {
                            const propSnap = await getDoc(doc(db, "properties", pId));
                            if (propSnap.exists()) {
                                setPropData({ id: propSnap.id, ...propSnap.data() });
                            }
                        }
                    }
                } catch (error) {
                    console.error("Auth Fetch Error:", error);
                }
            } else {
                setLoading(false);
            }
        });
        return () => unsub();
    }, []);

    // ================= STEP 2: REAL-TIME DATA LISTENER (DATE-DRIVEN) =================
    useEffect(() => {
        if (!propertyId) return;

        setLoading(true);
        const q = query(
            collection(db, "reservations"),
            where("propertyId", "==", propertyId),
            where("checkoutdate", "==", selectedDate),
            where("status", "in", ["Booked", "Occupied", "Live", "Checkout"])
        );

        const unsubscribe = onSnapshot(q, (snapshot) => {
            const data = snapshot.docs.map((d) => ({
                id: d.id,
                ...d.data(),
            }));
            setBookings(data);
            setLoading(false);
        }, (error) => {
            console.error("Checkout Fetch Error:", error);
            Toast.fire({ icon: "error", title: "Failed to load checkouts" });
            setLoading(false);
        });

        return () => unsubscribe();
    }, [propertyId, selectedDate]);

    // ================= STEP 3: OPTIMIZED SEARCH =================
    const filteredBookings = useMemo(() => {
        return bookings.filter((b) =>
            b.customers?.custname?.toLowerCase().includes(searchText.toLowerCase()) ||
            b.customers?.mobile?.includes(searchText) ||
            b.rooms?.some(r => String(r.roomno || "").toLowerCase().includes(searchText.toLowerCase()))
        );
    }, [searchText, bookings]);

    // Financial calculations for the day's checkout list
    const summaryTotals = useMemo(() => {
        let totalVal = 0;
        let totalPaid = 0;
        let totalDue = 0;
        let totalRooms = 0;

        filteredBookings.forEach((b) => {
            const tot = Number(b.totalamount || b.rooamount || 0);
            const adv = Number(b.advance || 0);
            const rem = Number(b.remaining !== undefined ? b.remaining : tot - adv);

            totalVal += tot;
            totalPaid += adv;
            totalDue += rem > 0 ? rem : 0;
            totalRooms += (b.rooms?.length || 1);
        });

        return { totalVal, totalPaid, totalDue, totalRooms };
    }, [filteredBookings]);

    const handlePrint = () => {
        window.print();
    };

    // UI Table Styling
    const cellStyle = {
        color: COLORS.vanilla,
        borderColor: COLORS.borderGreen,
        fontSize: "12px",
        padding: "10px 12px"
    };

    const headerCellStyle = {
        backgroundColor: COLORS.calPolyGreen,
        color: COLORS.gold,
        fontWeight: "bold",
        fontSize: "12px",
        borderColor: COLORS.darkGreen,
        padding: "10px 12px",
        textTransform: "uppercase"
    };

    return (
        <Box sx={{ background: COLORS.bgDark, minHeight: "100vh", p: { xs: 1.5, md: 3 } }}>
            {/* Integrated Print CSS Rules */}
            <style>
                {`
                @media print {
                    html, body {
                        background: #ffffff !important;
                        color: #000000 !important;
                        height: auto !important;
                        overflow: visible !important;
                        margin: 0 !important;
                        padding: 0 !important;
                    }

                    .no-print {
                        display: none !important;
                    }

                    /* Force the main invoice wrapper to span full width */
                    .invoice-print-wrapper {
                        width: 100% !important;
                        max-width: 100% !important;
                        flex-basis: 100% !important;
                        margin: 0 !important;
                        padding: 0 !important;
                        background: #ffffff !important;
                        color: #000000 !important;
                        box-shadow: none !important;
                        border: none !important;
                    }

                    /* Ensure specific borders are visible for structure */
                    .print-area {
                        border: none !important;
                        background: #ffffff !important;
                        color: #000000 !important;
                        box-shadow: none !important;
                    }

                    /* Fix table printing */
                    table {
                        width: 100% !important;
                        border-collapse: collapse !important;
                    }
                    
                    th, td {
                        border: 1px solid #ddd !important;
                        padding: 8px !important;
                        color: #000000 !important;
                    }

                    th {
                        background-color: #f5f5f5 !important;
                        -webkit-print-color-adjust: exact !important;
                        print-color-adjust: exact !important;
                        font-weight: bold !important;
                    }

                    .hotel-title, .print-text, p, span, h5, h6 {
                        color: #000000 !important;
                    }

                    .MuiPaper-root {
                        background-color: transparent !important;
                        box-shadow: none !important;
                        border: none !important;
                    }
                }
                `}
            </style>

            <Container maxWidth="xl" className="invoice-print-wrapper">
                <div ref={printRef} className="print-area">
                    {/* PRINT HOTEL HEADER */}
                    <Box textAlign="center" mb={2} sx={{ display: { xs: "none", sm: "block" } }}>
                        <Typography
                            variant="h5"
                            fontWeight="800"
                            className="hotel-title"
                            sx={{ color: COLORS.gold, letterSpacing: "1px", textTransform: "uppercase" }}
                        >
                            {propData.hotelname || "Premier Property"}
                        </Typography>
                        <Typography variant="subtitle2" className="print-text" sx={{ color: COLORS.vanilla, fontWeight: 600, mt: 0.5 }}>
                            GSTIN: {propData.gstno || "N/A"}
                        </Typography>
                        <Typography variant="body2" className="print-text" sx={{ color: TEXT_SECONDARY, mt: 0.5 }}>
                            {[propData.address, propData.city, propData.state, propData.pincode].filter(Boolean).join(", ")}
                            {" | "}Phone: +91 {propData.mobile || "N/A"}
                        </Typography>
                    </Box>

                    {/* INTERACTIVE CONTROLS (HIDDEN ON PRINT) */}
                    <Box
                        display="flex"
                        justifyContent="space-between"
                        alignItems="center"
                        flexWrap="wrap"
                        gap={2}
                        mb={3}
                        className="no-print"
                    >
                        <Box>
                            <Typography variant="h5" sx={{ fontWeight: 800, color: COLORS.gold, textTransform: "uppercase", letterSpacing: "0.5px" }}>
                                Checkout Departures Report
                            </Typography>
                            <Typography variant="body2" sx={{ color: TEXT_SECONDARY }}>
                                Scheduled departures list for selected date
                            </Typography>
                        </Box>

                        <Box display="flex" alignItems="center" gap={1.5} flexWrap="wrap">
                            <Box sx={{ background: COLORS.darkGreen, p: "4px 12px", borderRadius: "10px", border: `1px solid ${COLORS.calPolyGreen}` }}>
                                <Typography variant="caption" sx={{ color: TEXT_SECONDARY, display: "block", fontWeight: 700, textTransform: "uppercase" }}>
                                    Select Departure Date
                                </Typography>
                                <input
                                    type="date"
                                    value={selectedDate}
                                    onChange={(e) => setSelectedDate(e.target.value)}
                                    style={{
                                        border: "none",
                                        outline: "none",
                                        background: "transparent",
                                        color: COLORS.gold,
                                        fontWeight: "700",
                                        fontSize: "13px"
                                    }}
                                />
                            </Box>

                            <Chip
                                label={`Staff: ${username || 'Loading...'}`}
                                variant="outlined"
                                sx={{
                                    borderColor: COLORS.calPolyGreen,
                                    color: COLORS.vanilla,
                                    fontWeight: 700,
                                    background: COLORS.darkGreen
                                }}
                            />

                            <Button
                                variant="contained"
                                onClick={handlePrint}
                                sx={{
                                    background: `linear-gradient(135deg, ${COLORS.gold}, ${COLORS.goldenrod})`,
                                    color: COLORS.darkGreen,
                                    fontWeight: "800",
                                    padding: "8px 22px",
                                    borderRadius: "10px",
                                    "&:hover": { background: COLORS.gold }
                                }}
                            >
                                🖨️ Print Checkouts
                            </Button>
                        </Box>
                    </Box>

                    {/* MAIN CARD CONTAINER */}
                    <Card
                        sx={{
                            borderRadius: "16px",
                            border: `1px solid ${COLORS.calPolyGreen}`,
                            background: COLORS.darkGreen,
                            boxShadow: "0 6px 18px rgba(0,0,0,0.3)"
                        }}
                        className="print-area"
                    >
                        {loading && <LinearProgress sx={{ backgroundColor: COLORS.calPolyGreen, "& .MuiLinearProgress-bar": { backgroundColor: COLORS.gold } }} />}
                        
                        <CardContent sx={{ p: { xs: 1.5, md: 3 } }}>
                            {/* SEARCH BAR (HIDDEN ON PRINT) */}
                            <Box className="no-print" mb={2.5}>
                                <TextField
                                    placeholder="Search by customer name, mobile, or room..."
                                    variant="outlined"
                                    size="small"
                                    fullWidth
                                    value={searchText}
                                    onChange={(e) => setSearchText(e.target.value)}
                                    sx={{
                                        maxWidth: 450,
                                        background: COLORS.bgDark,
                                        borderRadius: "10px",
                                        "& .MuiOutlinedInput-notchedOutline": { borderColor: COLORS.calPolyGreen },
                                        "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: COLORS.gold },
                                        "& input": { color: COLORS.vanilla, fontSize: "13px" }
                                    }}
                                />
                            </Box>

                            {/* DEPARTURE METADATA STRIP */}
                            <TableContainer
                                component={Paper}
                                sx={{
                                    mb: 2.5,
                                    background: COLORS.bgDark,
                                    border: `1px solid ${COLORS.calPolyGreen}`,
                                    borderRadius: "10px"
                                }}
                            >
                                <Table size="small">
                                    <TableBody>
                                        <TableRow>
                                            <TableCell align="left" width="33%" sx={cellStyle}>
                                                <Typography variant="body2" className="print-text" sx={{ color: COLORS.vanilla }}>
                                                    <strong>Departure Date:</strong> {moment(selectedDate).format("DD-MM-YYYY")}
                                                </Typography>
                                            </TableCell>
                                            <TableCell align="center" width="34%" sx={cellStyle}>
                                                <Typography variant="subtitle2" fontWeight="800" className="print-text" sx={{ color: COLORS.gold, textTransform: "uppercase" }}>
                                                    Total Departures: {filteredBookings.length} ({summaryTotals.totalRooms} Rooms)
                                                </Typography>
                                            </TableCell>
                                            <TableCell align="right" width="33%" sx={cellStyle}>
                                                <Typography variant="body2" className="print-text" sx={{ color: COLORS.vanilla }}>
                                                    <strong>Pending Due:</strong> ₹{summaryTotals.totalDue.toLocaleString("en-IN")}
                                                </Typography>
                                            </TableCell>
                                        </TableRow>
                                    </TableBody>
                                </Table>
                            </TableContainer>

                            {/* CHECKOUT TABLE */}
                            <TableContainer
                                component={Paper}
                                sx={{
                                    background: COLORS.bgDark,
                                    border: `1px solid ${COLORS.calPolyGreen}`,
                                    borderRadius: "10px",
                                    overflowX: "auto"
                                }}
                            >
                                <Table size="medium">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell sx={headerCellStyle}>Sr</TableCell>
                                            <TableCell sx={headerCellStyle}>Guest Information</TableCell>
                                            <TableCell sx={headerCellStyle}>Stay Period</TableCell>
                                            <TableCell sx={headerCellStyle}>Room & Plan</TableCell>
                                            <TableCell sx={headerCellStyle}>Status</TableCell>
                                            <TableCell sx={headerCellStyle} align="right">Financial Status</TableCell>
                                        </TableRow>
                                    </TableHead>

                                    <TableBody>
                                        {filteredBookings.length > 0 ? (
                                            filteredBookings.map((item, index) => {
                                                const totalAmt = Number(item.totalamount || item.rooamount || 0);
                                                const advanceAmt = Number(item.advance || 0);
                                                const dueAmt = Number(item.remaining !== undefined ? item.remaining : totalAmt - advanceAmt);

                                                return (
                                                    <TableRow key={item.id} hover>
                                                        <TableCell sx={cellStyle}>{index + 1}</TableCell>

                                                        <TableCell sx={cellStyle}>
                                                            <Typography variant="subtitle2" className="print-text" sx={{ textTransform: "capitalize", fontWeight: 700, color: COLORS.vanilla }}>
                                                                {item.customers?.custname || "Guest"}
                                                            </Typography>
                                                            <Typography variant="caption" className="print-text" sx={{ color: TEXT_SECONDARY }}>
                                                                {item.customers?.mobile || "No Contact"}
                                                            </Typography>
                                                        </TableCell>

                                                        <TableCell sx={cellStyle}>
                                                            <Typography variant="body2" className="print-text" sx={{ color: COLORS.vanilla }}>
                                                                {moment(item.checkindate).format("DD/MM/YYYY")} ➔ {moment(item.checkoutdate).format("DD/MM/YYYY")}
                                                            </Typography>
                                                            <Typography variant="caption" sx={{ color: COLORS.gold, fontWeight: 700 }}>
                                                                {item.adults || 2} Adults {item.childs ? `/ ${item.childs} Children` : ""}
                                                            </Typography>
                                                        </TableCell>

                                                        <TableCell sx={cellStyle}>
                                                            {item.rooms?.map((r, i) => (
                                                                <Chip
                                                                    key={i}
                                                                    label={`Room ${r.roomno || r.roomname} (${r.planname || 'EP'})`}
                                                                    size="small"
                                                                    sx={{
                                                                        m: 0.3,
                                                                        fontSize: "11px",
                                                                        fontWeight: 700,
                                                                        background: COLORS.calPolyGreen,
                                                                        color: COLORS.vanilla,
                                                                        border: `1px solid ${COLORS.gold}`
                                                                    }}
                                                                />
                                                            ))}
                                                        </TableCell>

                                                        <TableCell sx={cellStyle}>
                                                            <span
                                                                style={{
                                                                    fontSize: "10px",
                                                                    fontWeight: "800",
                                                                    textTransform: "uppercase",
                                                                    padding: "4px 8px",
                                                                    borderRadius: "8px",
                                                                    background: item.status === "Checkout" ? "rgba(255, 213, 0, 0.15)" : COLORS.calPolyGreen,
                                                                    color: item.status === "Checkout" ? COLORS.gold : COLORS.vanilla,
                                                                    border: `1px solid ${COLORS.calPolyGreen}`
                                                                }}
                                                            >
                                                                {item.status || "Booked"}
                                                            </span>
                                                        </TableCell>

                                                        <TableCell align="right" sx={cellStyle}>
                                                            <Box>
                                                                <Typography variant="body2" className="print-text" sx={{ color: COLORS.vanilla }}>
                                                                    Total: ₹{totalAmt.toFixed(2)}
                                                                </Typography>
                                                                <Typography variant="caption" className="print-text" sx={{ color: COLORS.goldenrod, display: "block" }}>
                                                                    Paid: ₹{advanceAmt.toFixed(2)}
                                                                </Typography>
                                                                <Typography
                                                                    variant="subtitle2"
                                                                    className="print-text"
                                                                    sx={{
                                                                        color: dueAmt > 0 ? "#ff6b6b" : COLORS.gold,
                                                                        fontWeight: 800
                                                                    }}
                                                                >
                                                                    Due: ₹{dueAmt.toFixed(2)}
                                                                </Typography>
                                                            </Box>
                                                        </TableCell>
                                                    </TableRow>
                                                );
                                            })
                                        ) : (
                                            <TableRow>
                                                <TableCell colSpan={6} align="center" sx={{ py: 4, color: TEXT_SECONDARY }}>
                                                    {loading ? "Fetching departure details..." : `No checkouts scheduled for ${moment(selectedDate).format("DD MMMM YYYY")}.`}
                                                </TableCell>
                                            </TableRow>
                                        )}

                                        {/* TOTAL ROW */}
                                        {filteredBookings.length > 0 && (
                                            <TableRow style={{ background: COLORS.calPolyGreen }}>
                                                <TableCell colSpan={3} align="right" sx={{ ...cellStyle, fontWeight: "bold", color: COLORS.gold }}>
                                                    TOTAL DEPARTURES
                                                </TableCell>
                                                <TableCell sx={{ ...cellStyle, fontWeight: "bold", color: COLORS.gold }}>
                                                    {summaryTotals.totalRooms} Rooms
                                                </TableCell>
                                                <TableCell sx={{ ...cellStyle, fontWeight: "bold", color: COLORS.gold }}>
                                                    -
                                                </TableCell>
                                                <TableCell align="right" sx={{ ...cellStyle, fontWeight: "800", color: COLORS.gold }}>
                                                    Due: ₹{summaryTotals.totalDue.toLocaleString("en-IN")}
                                                </TableCell>
                                            </TableRow>
                                        )}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </CardContent>
                    </Card>
                </div>
            </Container>
        </Box>
    );
}

export default Checkoutreport;